import { createDialog, type DialogService } from '@patsnap/tahiti-core';
import { injectable } from '@patsnap/tahiti-core/shared/inversify';
import type {
    HubspotDialogOptions,
    HubspotDialogResult,
} from '@/presentation/components/hubspot/HubspotDialog.vue';

// Re-export types for external use
export type { HubspotDialogOptions, HubspotDialogResult };

/**
 * HubSpot 对话框服务工厂
 * 基于 createDialog 创建对话框实例
 */
export const hubspotDialogServiceFactory = createDialog(() => import('@/presentation/components/hubspot/HubspotDialog.vue'));

/**
 * HubSpot 对话框服务接口
 */
export interface HubspotDialogService {
    /**
     * 打开 HubSpot 表单对话框
     */
    openDialog(options?: HubspotDialogOptions): Promise<HubspotDialogResult>;

    /**
     * 打开简单的联系我们对话框
     */
    openContactUsDialog(title?: string): Promise<HubspotDialogResult>;
}

/**
 * HubSpot 对话框服务实现
 */
@injectable()
export class HubspotDialogServiceImpl implements HubspotDialogService {
    private _dialogService = hubspotDialogServiceFactory();

    /**
     * 打开 HubSpot 表单对话框
     * @param options 对话框配置选项
     * @returns Promise<HubspotDialogResult> 对话框结果
     */
    async openDialog(options: HubspotDialogOptions = {}): Promise<HubspotDialogResult> {
        const dialog = this._dialogService;
        return new Promise((resolve, reject) => {
            // 设置对话框回调
            dialog.onSubmit(async (result, next) => {
                try {
                    // 处理提交逻辑
                    resolve(result);
                    next(); // 关闭对话框
                } catch (error) {
                    // 如果有错误，保持对话框打开
                    next(false);
                    reject(error);
                }
            });

            dialog.onCancel(() => {
                // 用户取消操作
                const result: HubspotDialogResult = {
                    action: 'cancel',
                };
                resolve(result);
            });

            // 打开对话框
            dialog.open(options);
        });
    }

    /**
     * 打开简单的联系我们对话框
     * @param title 对话框标题
     * @returns Promise<HubspotDialogResult> 对话框结果
     */
    async openContactUsDialog(title?: string): Promise<HubspotDialogResult> {
        return this.openDialog({
            title: title || 'Contact Us',
            width: '600px',
            height: '500px',
            showCloseButton: true,
        });
    }
}

// 导出服务 Symbol
export const HubspotDialogService = Symbol.for('HubspotDialogService');
